import type {WellAllocationDetails} from "@ots-frontend/ots-data";
import {useState} from "react";
import {OtsActionButton} from "../components/OtsButton";
import {OtsInlineAlert} from "../components/OtsInlineAlert";
import {AllocationCalculationSummary} from "./AllocationCalculationSummary";
import {AllocationSankey} from "./AllocationSankey";
import {WellAllocationTable} from "./WellAllocationTable";

export type WellAllocationInspectorProps = {
  details?: WellAllocationDetails;
  loading?: boolean;
  error?: string;
  volumeUnit?: string;
  onRetry?: () => void;
  defaultAdvanced?: boolean;
  compact?: boolean;
};

/** Full allocation explanation, reusable in a dialog or a future ticket detail page. */
export const WellAllocationInspector = ({details, loading, error, volumeUnit, onRetry, defaultAdvanced = false, compact = false}: WellAllocationInspectorProps) => {
  const [advanced, setAdvanced] = useState(defaultAdvanced);
  if (loading) {
    return (
      <div role="status" className="grid gap-4">
        <div className="h-28 animate-pulse rounded-xl bg-[var(--ots-surface-variant)]"/>
        <div className="h-72 animate-pulse rounded-xl bg-[var(--ots-surface-variant)]"/>
        <span className="sr-only">Loading well allocation</span>
      </div>
    );
  }

  if (error) {
    return <OtsInlineAlert tone="danger" title="Well allocation could not be loaded" action={onRetry ? <button type="button" onClick={onRetry} className="font-semibold underline">Try again</button> : undefined}>{error}</OtsInlineAlert>;
  }

  if (!details) return null;

  if (details.allocations.length === 0) {
    return <OtsInlineAlert tone="info" title="No eligible wells">The resolved allocation method did not produce any eligible wells for this ticket.</OtsInlineAlert>;
  }

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      <AllocationCalculationSummary configuration={details.configuration} eligibleWellCount={details.allocations.length}/>
      <section>
        <div className={compact ? "mb-2" : "mb-3"}>
          <h3 className={['font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-secondary)]', compact ? "text-base" : "text-lg"].join(" ")}>Cost split</h3>
          <p className={['mt-1 text-[var(--ots-text-muted)]', compact ? "text-xs" : "text-sm"].join(" ")}>Flow width represents the final allocation percentage. Zero-percent wells stay visible because they were eligible for the calculation.</p>
        </div>
        <AllocationSankey allocations={details.allocations} viewportHeight={compact ? 390 : 520}/>
      </section>
      <section>
        <div className={["flex flex-wrap items-end justify-between gap-3", compact ? "mb-2" : "mb-3"].join(" ")}>
          <div>
            <h3 className={['font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-secondary)]', compact ? "text-base" : "text-lg"].join(" ")}>Production reconciliation</h3>
            <p className={['mt-1 text-[var(--ots-text-muted)]', compact ? "text-xs" : "text-sm"].join(" ")}>Every eligible well remains in the result, including wells allocated at 0%.</p>
          </div>
          <OtsActionButton
            size="compact"
            variant={advanced ? "tonal" : "text"}
            aria-pressed={advanced}
            onClick={() => setAdvanced((value) => !value)}>
            {advanced ? "Hide advanced" : "Advanced"}
          </OtsActionButton>
        </div>
        <WellAllocationTable
          allocations={details.allocations}
          volumeUnit={volumeUnit}
          timeZone={details.configuration.timeZone}
          advanced={advanced}
          compact={compact}/>
      </section>

      <aside className="border-t border-[var(--ots-outline-variant)] pt-3 text-xs text-[var(--ots-text-muted)]">
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <span><strong className="text-[var(--ots-text)]">1 · Eligible wells</strong> come from the resolved {details.configuration.eligibilityMethod.toLowerCase().replace(/_/g, " ")} method.</span>
          <span><strong className="text-[var(--ots-text)]">2 · Production</strong> is normalized across the configured window when needed.</span>
          <span><strong className="text-[var(--ots-text)]">3 · Allocation</strong> divides each adjusted volume by the adjusted total.</span>
        </div>
      </aside>
    </div>
  );
};
